import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Typography,
  Chip,
  Box,
  Stack,
  LinearProgress,
  Avatar,
  Tooltip,
} from '@mui/material';
import { School, AccessTime, Person } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Card from './UIKit/Card';
import { AUTH_SERVICE_URL } from '../config';

// Constants
const MAX_VISIBLE_PROFESSIONS = 2;

// Функция для определения цвета сложности (не зависит от состояния)
const getDifficultyColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'начинающий':
    case 'beginner':
      return 'success';
    case 'средний':
    case 'intermediate':
      return 'warning';
    case 'продвинутый':
    case 'advanced':
      return 'error';
    default:
      return 'default';
  }
};

// Функция для обработки пути к аватару (не зависит от состояния)
const getAvatarUrl = (avatarPath) => {
  if (!avatarPath) return null;

  // Проверяем, является ли путь относительным (начинается с /)
  if (avatarPath.startsWith('/') && !avatarPath.startsWith('//')) {
    return `${AUTH_SERVICE_URL}${avatarPath}`;
  }

  // Если путь абсолютный (содержит http или https), возвращаем как есть
  return avatarPath;
};

const LearningPathCard = ({ learningPath, progress = null }) => {
  const { t } = useTranslation(['common', 'learningPaths']);

  const {
    _id,
    title,
    description,
    image,
    difficulty,
    tags,
    estimatedTimeMinutes,
    courses,
    createdBy,
    isPublished,
    professions,
  } = learningPath;

  // Функция для форматирования времени в часы и минуты
  const formatTimeHoursMinutes = useCallback((minutes) => {
    if (!minutes || minutes <= 0) return null;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0 && remainingMinutes > 0) {
      return `${hours} ${t('common:time.shortHour')} ${remainingMinutes} ${t(
        'common:time.shortMin'
      )}`;
    } else if (hours > 0) {
      return `${hours} ${t('common:time.shortHour')}`;
    } else {
      return `${remainingMinutes} ${t('common:time.shortMin')}`;
    }
  }, [t]);

  // Получаем данные автора из обновленной структуры
  const getAuthorData = useCallback(() => {
    if (!createdBy) return { name: t('common:roles.admin'), avatar: null };

    // Проверяем наличие вложенной структуры userMeta
    if (createdBy.userMeta) {
      return {
        name:
          createdBy.userMeta.fullName ||
          createdBy.userMeta.email ||
          t('common:roles.admin'),
        avatar:
          getAvatarUrl(createdBy.userMeta.avatar) ||
          getAvatarUrl(createdBy.avatar) ||
          null,
      };
    }

    // Старая структура
    return {
      name: createdBy.name || t('common:roles.admin'),
      avatar: getAvatarUrl(createdBy.avatar) || null,
    };
  }, [createdBy, t]);

  // Функция для получения имени профессии
  const getProfessionName = useCallback((profession) => {
    if (!profession) return t('common:status.noProfession');

    if (typeof profession === 'string') return profession;

    if (profession.term_group && profession.term_group.MainTerm && profession.term_group.MainTerm.value) {
      return profession.term_group.MainTerm.value;
    }

    return profession.name || t('common:status.noProfession');
  }, [t]);

  // Рендер чипов профессий с индикатором переполнения
  const renderProfessionChips = useCallback(() => {
    if (
      !professions ||
      !Array.isArray(professions) ||
      professions.length === 0
    ) {
      return (
        <Chip
          label={t('common:status.noProfession')}
          size="small"
          color="primary"
          variant="outlined"
        />
      );
    }

    // Фильтруем null и undefined значения
    const validProfessions = professions.filter(
      (prof) => prof !== null && prof !== undefined
    );

    // Показываем только ограниченное количество профессий
    const visibleProfessions = validProfessions.slice(
      0,
      MAX_VISIBLE_PROFESSIONS
    );
    const remainingCount = validProfessions.length - MAX_VISIBLE_PROFESSIONS;

    return (
      <>
        {visibleProfessions.map((profession, index) => (
          <Tooltip
            title={getProfessionName(profession)}
            key={
              typeof profession === 'object'
                ? profession.id || profession._id || index
                : profession || index
            }
            placement="top"
          >
            <Chip
              label={getProfessionName(profession)}
              size="small"
              color="primary"
              sx={{
                maxWidth: 'calc(50% - 8px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            />
          </Tooltip>
        ))}

        {remainingCount > 0 && (
          <Tooltip
            title={validProfessions
              .slice(MAX_VISIBLE_PROFESSIONS)
              .map((p) => getProfessionName(p))
              .join(', ')}
            placement="top"
          >
            <Chip label={`+${remainingCount}`} size="small" color="primary" />
          </Tooltip>
        )}
      </>
    );
  }, [professions, getProfessionName, t]);

  if (!_id) {
    console.error('Учебная дорожка без ID:', learningPath);
    return null;
  }

  // Получаем данные автора
  const authorData = getAuthorData();

  return (
    <Card
      component={Link}
      to={`/learning-paths/${_id}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%',
        overflow: 'hidden',
        transition: 'transform 0.3s, box-shadow 0.3s',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
        },
        cursor: 'pointer',
        borderRadius: '12px',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <Box
        sx={{
          height: '160px',
          backgroundImage: `url(${image || '/images/default-path.jpg'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          borderRadius: '8px',
        }}
      >
        {difficulty && (
          <Box position="absolute" top="12px" right="12px">
            <Chip
              size="small"
              label={difficulty}
              color={getDifficultyColor(difficulty)}
              sx={{ fontWeight: 600 }}
            />
          </Box>
        )}

        {isPublished === false && (
          <Box position="absolute" top="12px" left="12px">
            <Chip
              size="small"
              label={t('common:status.draft')}
              color="primary"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '1.2rem',
            marginBottom: '12px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            height: '60px',
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            marginBottom: '12px',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            height: '70px',
          }}
        >
          {description}
        </Typography>

        {tags && tags.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '12px',
            }}
          >
            {tags.slice(0, 3).map((tag, index) => (
              <Chip key={index} label={tag} size="small" variant="outlined" />
            ))}
            {tags.length > 3 && (
              <Chip
                label={`+${tags.length - 3}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        )}

        <Stack direction="row" spacing={2} my={1} alignItems="center">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.85rem',
              color: 'text.secondary',
            }}
          >
            <School fontSize="small" />
            <Typography variant="body2">
              {courses.length} {t('common:stats.courses')}
            </Typography>
          </Box>

          {estimatedTimeMinutes > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '0.85rem',
                color: 'text.secondary',
              }}
            >
              <AccessTime fontSize="small" />
              <Typography variant="body2">
                {formatTimeHoursMinutes(estimatedTimeMinutes)}
              </Typography>
            </Box>
          )}
        </Stack>

        {progress !== null && (
          <Box mt={1} mb={2}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t('common:stats.progress')}
              </Typography>
              <Typography variant="caption" color="primary" fontWeight="bold">
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* Профессии (аналогично CourseCard.js) */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'nowrap',
            minHeight: '32px',
            alignItems: 'flex-end',
            overflow: 'hidden',
            mt: progress ? 0 : 'auto',
            marginBottom: '12px',
          }}
        >
          {renderProfessionChips()}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 'auto',
            paddingTop: '12px',
          }}
        >
          {createdBy && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {authorData.avatar ? (
                <Avatar
                  src={authorData.avatar}
                  alt={authorData.name}
                  sx={{ width: 24, height: 24 }}
                />
              ) : (
                <Person fontSize="small" />
              )}
              <Typography variant="caption">{authorData.name}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default LearningPathCard;
